"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade } from "swiper/modules";
import { buildContentstackImageUrl } from "@/lib/utils";
import { useGalleryImages } from "./useGalleryImages";
import { useModal } from "@/components/ModalContext";
import GalleryLightbox from "./GalleryLightbox";
import GalleryBadgeTooltip from "./GalleryBadgeTooltip";
import GalleryAccolades from "./GalleryAccolades";
import GalleryPromoOverlay from "./GalleryPromoOverlay";
import NavButton from "./NavButton";
import "swiper/css";
import "swiper/css/effect-fade";

// Thumbnails render in a 96px box, so cap the source at 2x for retina.
const THUMB_WIDTH = 192;
// Largest `deviceSizes` entry in next.config.mjs. No srcset candidate is wider.
const MAIN_MAX_WIDTH = 2048;

// One bucket per `container` max-width (1024/1280/1536). The image paints at
// most 496/736/992. 520 and 760 add headroom; 1200 skips the 1080 srcset
// candidate so a 1x screen downloads the full 1200px file.
const DESKTOP_MAIN_SIZES = "(max-width: 1023px) 0px, (max-width: 1279px) 520px, (max-width: 1535px) 760px, 1200px";

const IMG_LAZY = { fetchPriority: "low", loading: "lazy" };
const IMG_EAGER = { fetchPriority: "auto", loading: "eager" };
// BL-275: fetchPriority alone doesn't reliably override loading="lazy" — use
// real preload for the LCP candidate.
const IMG_PRELOAD = { priority: true, fetchPriority: "high" };

// Fade keeps every slide stacked in the viewport, so loading="lazy" alone
// never defers anything here — mount only the active slide and its neighbours.
const shouldLoad = (index, active) => Math.abs(index - active) <= 1;

const MainImage = ({ image, index, sizes, priority = false }) => (
  <Image
    src={buildContentstackImageUrl(image.url, MAIN_MAX_WIDTH)}
    alt={image.alt || `Gallery image ${index + 1}`}
    fill
    sizes={sizes}
    className="object-cover"
    {...(priority ? IMG_PRELOAD : IMG_EAGER)}
  />
);

const Carousel = ({ images, badge, accolades }) => {
  const swiperRef = useRef(null);
  const [active, setActive] = useState(0);
  const currentImage = images[active];
  const { openModal } = useModal();

  const syncSlide = (swiper) => {
    swiperRef.current = swiper;
    setActive(swiper.realIndex);
  };

  const goTo = (index) => {
    setActive(index);
    swiperRef.current?.slideTo(index);
  };

  // `openModal` freezes the content at open time: it doesn't re-render when
  // this component's state changes. That's why the lightbox tracks its own
  // index and reports it back via callback, keeping the main carousel in
  // sync once it closes.
  const openLightbox = (index) =>
    openModal(
      <GalleryLightbox images={images} initialIndex={index} onIndexChange={goTo} />,
      "max-w-none",
      "bg-transparent",
      { ariaLabel: "Product image gallery", bare: true, hideDefaultCloseButton: true },
      "max-h-full",
    );
  const prev = () => goTo(Math.max(active - 1, 0));
  const next = () => goTo(Math.min(active + 1, images.length - 1));

  return (
    <div>
      {/* Desktop */}
      <div className="hidden lg:flex gap-4">
        {/* The scroller is absolute so this wrapper has no intrinsic height:
            the flex line is sized by the main image's aspect ratio, and the
            thumbnails overflow inside it instead of stretching the grid. */}
        <div className="relative w-24 shrink-0">
          <div className="absolute inset-0 flex flex-col gap-4 overflow-y-auto">
            {images.map((image, index) => (
              <button
                key={image.uid || index}
                type="button"
                onClick={() => goTo(index)}
                aria-current={index === active ? "true" : undefined}
                className={`relative w-24 h-16 shrink-0 overflow-hidden transition-[border-color,opacity] cursor-pointer ${
                  index === active
                    ? "border-sea border-[2.5px]"
                    : "border border-sand opacity-50 hover:opacity-100"
                }`}
              >
                <Image
                  src={buildContentstackImageUrl(image.url, THUMB_WIDTH)}
                  alt={image.alt || `Thumbnail ${index + 1}`}
                  fill
                  sizes="96px"
                  className="object-cover"
                  {...IMG_LAZY}
                />
              </button>
            ))}
          </div>
        </div>

        <div className="relative flex-1">
          <div className="relative aspect-4/3 bg-white overflow-hidden">
            {/* Hidden on mobile, but display:none doesn't stop non-lazy
                fetches — 0px keeps that side's srcset candidate tiny.
                Fixed LCP slot: this is the preload candidate on load. Later
                slide changes re-preload the new src, which competes with
                nothing by then. */}
            <button
              type="button"
              onClick={() => openLightbox(active)}
              aria-label={`View ${currentImage.alt || `image ${active + 1}`} full size`}
              className="absolute inset-0 h-full w-full cursor-zoom-in"
            >
              <MainImage
                image={currentImage}
                index={active}
                sizes={DESKTOP_MAIN_SIZES}
                priority
              />
            </button>
          </div>

          {/* Both self-positioned (absolute) and return null without data. */}
          {active === 0 && <GalleryAccolades accolades={accolades} />}
          <GalleryBadgeTooltip badge={badge} />
          {active === 0 && <GalleryPromoOverlay />}
          <div className="absolute bottom-[12.5px] 2xl:bottom-5 right-6 z-20 flex gap-6">
            <NavButton direction="prev" onClick={prev} disabled={active === 0} />
            <NavButton direction="next" onClick={next} disabled={active === images.length - 1} />
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="lg:hidden relative bg-white">
        <div className="relative aspect-4/3">
          <GalleryAccolades accolades={accolades} visible={active === 0} />
          <GalleryPromoOverlay visible={active === 0} />
          <Swiper
            modules={[EffectFade]}
            effect="fade"
            fadeEffect={{ crossFade: true }}
            slidesPerView={1}
            onSwiper={syncSlide}
            onSlideChange={syncSlide}
            className="h-full"
          >
            {images.map((image, index) => (
              <SwiperSlide key={image.uid || index}>
                <div className="relative h-full bg-white">
                  {shouldLoad(index, active) && (
                    <button
                      type="button"
                      onClick={() => openLightbox(index)}
                      aria-label={`View ${image.alt || `image ${index + 1}`} full size`}
                      className="absolute inset-0 h-full w-full cursor-zoom-in"
                    >
                      <MainImage
                        image={image}
                        index={index}
                        sizes="(max-width: 1023px) 100vw, 1px"
                        priority={index === 0}
                      />
                    </button>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <NavButton
            direction="prev"
            onClick={prev}
            disabled={active === 0}
            variant="mobile"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-20"
          />
          <NavButton
            direction="next"
            onClick={next}
            disabled={active === images.length - 1}
            variant="mobile"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-20"
          />
        </div>

        <div className="w-full py-6 px-4 bg-warm-white">
          <div className="relative w-full h-1 bg-sand rounded-full overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-sea rounded-full transition-transform duration-300 ease-out"
              style={{
                width: `${100 / images.length}%`,
                transform: `translate3d(${active * 100}%, 0, 0)`,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const ProductCarousel = ({ images: imagesProp, badge, accolades }) => {
  const { images, galleryKey } = useGalleryImages(imagesProp);
  if (!images.length) return null;
  return <Carousel key={galleryKey} images={images} badge={badge} accolades={accolades} />;
};

export default ProductCarousel;
